import React, { useEffect, useState, useRef, useMemo } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Switch,
  Chip,
} from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import MUIDataTable from "mui-datatables";
import AddIcon from "@mui/icons-material/Add";
import Loader from "../../../components/loader/Loader";
import { useForm } from "react-hook-form";
import EditIcon from "@mui/icons-material/Edit";
import { yupResolver } from "@hookform/resolvers/yup";
import { grey, orange } from "@mui/material/colors";
import { FormInputText } from "../../../components/form-components/FormInputText";
import SaveIcon from "@mui/icons-material/Save";
import { categorySchema } from "../../../app/schema";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CategoryImageUpload from "../../../components/form-components/CategoryImageUpload";
import { PhotoProvider, PhotoView } from "react-photo-view";

import {
  hideConfirmModal,
  showConfirmModal,
  showModal,
  storeCategoryList,
} from "../../../redux/counterSlice";

import { useDragAndDropTable } from "../../../redux/dragDrop";
import { FormColorPicker } from "../../../components/form-components/FormColorPicker";
import {
  useTablePagination,
  useTableHeight,
} from "../../../hooks/useTablePagination";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { usePermission } from "../../../hooks/usePermission";

function Category() {
  const dispatch = useDispatch();
  const permissions = usePermission("category");

  const {
    pageNo,
    pageSize,
    searchText,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
  } = useTablePagination("category", 10);
  const tableHeight = useTableHeight();
  const tableRef = useRef(null);
  const [formData, setFormData] = useState([]);
  const hasActionPermission = permissions?.edit || permissions?.delete;

  const handleUpdateOrder = (newData) => {
    setFormData(newData);

    const reorderedPayload = newData.map((item, index) => ({
      id: item.id,
      sort_order: index + 1, // assuming order starts at 1
    }));

    axios
      .post("/categories/order", reorderedPayload)
      .then((response) => {
        toast.success(
          response?.data?.message || "Category order updated successfully"
        );
      })
      .catch((error) => {
        toast.error(
          error.response?.data?.message || "Failed to update category order"
        );
      });
  };

  // Use drag-and-drop hook AFTER formData and handler are defined
  useDragAndDropTable({
    formData,
    pageNo,
    pageSize,
    onUpdateOrder: handleUpdateOrder,
    containerRef: tableRef,
  });

  const methods = useForm({
    resolver: yupResolver(categorySchema),
  });
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = methods;

  const [loader, setLoader] = useState(true);
  const [updating, setUpdating] = useState(false); // For updates that shouldn't unmount table
  const [open, setOpen] = useState(false);
  const [openImage, setOpenImage] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const currentPageRef = useRef(pageNo); // Track current page
  
  // Update ref when page changes
  useEffect(() => {
    currentPageRef.current = pageNo;
  }, [pageNo]);

  // Load initial categories - preservePage parameter to maintain current page
  const loadInitialData = (preservePage = false) => {
    // Use updating state for operations that should preserve page
    // Use loader state only for initial load
    if (preservePage) {
      setUpdating(true);
    } else {
      setLoader(true);
    }
    
    axios
      .get("/category")
      .then((response) => {
        setFormData(response?.data?.data || []);
        
        if (preservePage) {
          setUpdating(false);
        } else {
          setLoader(false);
        }
      })
      .catch((err) => {
        toast.error(err?.message);
        
        if (preservePage) {
          setUpdating(false);
        } else {
          setLoader(false);
        }
      });
  };

  // // Reload active category list in Redux store
  // const reloadStore = () => {
  //   axios
  //     .get("/getActiveCategory")
  //     .then((response) => {
  //       if (response?.data?.data?.length > 0) {
  //         dispatch(storeCategoryList(response.data.data));
  //       }
  //     })
  //     .catch((err) => {
  //       toast.error(err?.message);
  //     });
  // };

  useEffect(() => {
    loadInitialData();
  }, []);

  const columns = [
    {
      name: "Drag",
      label: "Drag",
      options: {
        // filter: false,
        // sort: false,
        customBodyRenderLite: (dataIndex) => (
          <div className="drag-handle" style={{ cursor: "grab" }}>
            <DragIndicatorIcon
              sx={{
                color: grey[600],
                "&:active": { cursor: "grabbing", opacity: 0.6 },
              }}
            />
          </div>
        ),
      },
    },
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <p>{dataIndex + 1}</p>;
        },
      },
    },
    {
      name: "app_category_image",
      label: "Image",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const imageUrl = formData[dataIndex]?.app_category_image;
          return imageUrl ? (
            <PhotoProvider>
              <PhotoView key={dataIndex} src={imageUrl}>
                <img
                  src={imageUrl}
                  alt=""
                  style={{
                    width: "70px",
                    height: "70px",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                />
              </PhotoView>
            </PhotoProvider>
          ) : (
            <h3>-</h3>
          );
        },
      },
    },
    {
      name: "category_name",
      label: "Name",
    },
    {
      name: "counts",
      label: "Sub-Category | Product",
      options: {
        customBodyRenderLite: (dataIndex) => {
          return (
            <div>
              <Chip
                label={formData[dataIndex]?.sub_categories_count}
                color="primary"
                size="small"
              />{" "}
              &nbsp;|&nbsp;
              <Chip
                label={formData[dataIndex]?.products_count}
                color="secondary"
                size="small"
              />
            </div>
          );
        },
      },
    },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const status = formData[dataIndex]?.active_status;
          const id = formData[dataIndex]?.id;

          return (
            <Switch
              size="small"
              color={status === 1 ? "success" : "error"}
              checked={status === 1}
              onChange={() => {
                const confirmLabel = status === 1 ? "Deactivate" : "Activate";
                const action = () => toggleStatus(id);

                setSelectedRow({
                  ...formData[dataIndex],
                  action,
                  confirmLabel,
                });

                dispatch(showConfirmModal({ message: confirmLabel, action }));
              }}
              style={{ marginBottom: "5px" }}
            />
          );
        },
      },
    },
    ...(hasActionPermission
      ? [
          {
            name: "Actions",
            options: {
              filter: false,
              customBodyRenderLite: (dataIndex) => {
                const status = formData[dataIndex]?.active_status;
                const id = formData[dataIndex]?.id;

                return (
                  <div className="actionBtns">
                    <Button
                      disabled={status === 1}
                      aria-label="edit"
                      onClick={() => {
                        setSelectedRow({ ...formData[dataIndex] });
                        setOpenImage(true);
                      }}
                    >
                      <AddPhotoAlternateIcon
                        sx={{ color: status === 1 ? grey[600] : orange[500] }}
                        fontSize="medium"
                      />
                    </Button>
                    {permissions?.edit && (
                      <Button
                        disabled={status === 1}
                        aria-label="edit"
                        onClick={() => editClickHandler(formData[dataIndex])}
                      >
                        <EditIcon
                          sx={{ color: status === 1 ? grey[600] : "secondary" }}
                          fontSize="medium"
                        />
                      </Button>
                    )}
                    {permissions?.delete && (
                      <Button
                        disabled={status === 1}
                        aria-label="delete"
                        onClick={() => {
                          const confirmLabel = "Delete";
                          const action = () => deleteClickHandler(id);

                          setSelectedRow({
                            ...formData[dataIndex],
                            action,
                            confirmLabel,
                          });

                          dispatch(
                            showConfirmModal({ message: confirmLabel, action })
                          );
                        }}
                      >
                        <DeleteOutlineOutlinedIcon
                          sx={{ color: status === 1 ? grey[600] : "red" }}
                          fontSize="medium"
                        />
                      </Button>
                    )}
                  </div>
                );
              },
            },
          },
        ]
      : []),
  ];

  const handleClose = () => {
    setOpen(false);
    setOpenImage(false);
    setSelectedRow(null);
    reset();
  };

  const editClickHandler = (item) => {
    setOpen(true);
    setSelectedRow({ ...item });
  };

  const onSubmit = (data) => {
    saveForm(data);
  };

  const toggleStatus = async (id) => {
    try {
      setUpdating(true);
      await axios.get("/categorySwitch/" + id);
      
      // Reload data and preserve page
      await loadInitialData(true);
      
      dispatch(hideConfirmModal());
    } catch (err) {
      toast.error(err?.message);
      setUpdating(false);
    }
  };

  const saveForm = async (data) => {
    try {
      const isEdit = selectedRow?.id;
      setUpdating(true);
      
      let response;
      if (isEdit) {
        response = await axios.put("/category/" + selectedRow?.id, data);
      } else {
        response = await axios.post("/category", data);
      }

      if (!response?.data?.error) {
        toast.success(response?.data?.message);
        
        // Reload data - preserve page for edit
        await loadInitialData(isEdit);
        
        // Close dialog and reset form
        handleClose();
      } else {
        let errDesc = "";
        if (response?.data?.data) {
          Object.values(response?.data?.data).forEach((error) => {
            errDesc += error + "\n";
          });
        } else {
          errDesc = response?.data?.message;
        }

        dispatch(
          showModal({
            msg: errDesc,
            error: response?.data?.error,
          })
        );
        setUpdating(false);
      }
    } catch (error) {
      dispatch(
        showModal({
          msg: error?.message,
          error: true,
        })
      );
      setUpdating(false);
    }
  };

  const deleteClickHandler = async (id) => {
    try {
      setUpdating(true);
      let response;
      if (id) {
        response = await axios.delete("/category/" + id);
        if (!response?.data?.error) {
          toast.success(response?.data?.message);
          
          // Reload data and preserve current page
          await loadInitialData(true);
          
          // Close dialog and reset
          handleClose();
          dispatch(hideConfirmModal());
        } else {
          let errDesc = "";
          if (response?.data?.data) {
            Object.values(response?.data?.data).forEach((error) => {
              errDesc += error + "\n";
            });
          } else {
            errDesc = response?.data?.message;
          }

          dispatch(
            showModal({
              msg: errDesc,
              error: response?.data?.error,
            })
          );
          setUpdating(false);
        }
      }
    } catch (error) {
      dispatch(
        showModal({
          msg: error?.message,
          error: true,
        })
      );
      setUpdating(false);
    }
  };

  const CreateEditDialog = () => (
    <Dialog open={open} onClose={handleClose} maxWidth={"md"}>
      <DialogTitle className="text-primary">
        {selectedRow ? "Edit Category" : "New Category"}
      </DialogTitle>
      <DialogContent>
        <Grid container columnSpacing={{ xs: 5 }}>
          <Grid item xs={12}>
            <FormInputText
              name="category_name"
              control={control}
              label="Category Name"
              errors={errors}
              mandatory="true"
              defaultValue={selectedRow?.category_name || ""}
            />
          </Grid>

          <Grid item xs={12}>
            <FormInputText
              name="category_description"
              control={control}
              label="Category Description"
              errors={errors}
              mandatory="true"
              defaultValue={selectedRow?.category_description || ""}
            />
          </Grid>

          <Grid item xs={12}>
            <FormColorPicker
              name="bg_color"
              control={control}
              label="Background Color"
              type="single"
              errors={errors}
              mandatory="true"
              defaultValue={selectedRow?.bg_color || ""}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained" color="error">
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          startIcon={<SaveIcon />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );

  const UpdateImageDialog = () => (
    <Dialog open={openImage} onClose={handleClose} maxWidth={"xs"}>
      <DialogTitle className="text-primary">Update Image</DialogTitle>
      <Divider variant="middle" />

      <DialogContent>
        <Grid container columnSpacing={{ xs: 4 }}>
          <Grid item xs={12}>
            <CategoryImageUpload
              status={selectedRow?.app_category_image}
              name={selectedRow?.id?.toString()}
              control={control}
              label={selectedRow?.category_name}
              setValue={setValue}
              type="text"
              defaultValue={selectedRow?.app_category_image}
              loadInitialData={async () => {
                await loadInitialData(true); // Preserve page
                handleClose(); // Close dialog after upload
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained" color="error">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );

  const options = {
    selectableRows: false,
    filter: false,
    download: false,
    viewColumns: true,
    print: false,
    searchText: searchText,
    rowsPerPage: pageSize,
    page: pageNo,
    count: formData.length,
    fixedHeader: true,
    tableBodyHeight: tableHeight,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
    serverSide: false,
  };
  
  // Memoize the table data to maintain reference stability
  const tableData = useMemo(() => formData, [formData.length]);
  
  console.log(formData);
  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h5 className="text-dark mt-2 screen-heading">Category</h5>
          <div ref={tableRef}>
            <MUIDataTable
              data={tableData?.length > 0 ? tableData : []}
              columns={columns}
              options={options}
              title={
                permissions.add ? (
                  <Button
                    variant="contained"
                    onClick={() => {
                      setSelectedRow(null);
                      setOpen(true);
                      reset();
                    }}
                    size="small"
                    startIcon={<AddIcon />}
                  >
                    New Category
                  </Button>
                ) : null
              }
            />
          </div>
        </>
      )}
      <CreateEditDialog />
      <UpdateImageDialog />

      <Dialog
        open={useSelector((state) => state.counter.confirmModal.flag)}
        onClose={() => dispatch(hideConfirmModal())}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {selectedRow?.confirmLabel}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => dispatch(hideConfirmModal())}
            variant="contained"
            color="error"
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={() => selectedRow?.action()}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Category;